import { Theme } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import { GridSize, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { memo, PropsWithChildren } from 'react';
import Button from '../Button';
import Image from '../Image';
import { MuiButtonBase, MuiGrid } from '../material-ui';
import RichText from '../RichText';
import { IconTextGridProps } from './IconTextGrid.interface';
import useStyles from './IconTextGrid.styles';

const IconTexGridComponent = ({
  className,
  iconTextGrid = [],
  leftGroupCta,
  leftGroupCtaStyle,
  leftGroupDescription,
  leftGroupLogo,
  theme = Theme.light,
  orientation = 'row',
}: PropsWithChildren<IconTextGridProps>) => {
  const classes = useStyles({});
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const multiRowClass = iconTextGrid.length > 2 ? classes.gridMultiRow : {};
  const cta = (
    <Button
      className={classes.cta}
      theme={theme}
      outlined={leftGroupCtaStyle?.trim().toLowerCase() === 'secondary'}
      link={leftGroupCta}
    />
  );
  const displayLeftGroup =
    (leftGroupLogo && leftGroupLogo.original) || leftGroupDescription || leftGroupCta;

  const desktopDistribution = React.useMemo<{ left: GridSize; right: GridSize }>(
    () =>
      /column/.test(orientation)
        ? { left: 12, right: 12 }
        : {
            left: 4,
            right: displayLeftGroup ? 8 : 12,
          },
    [orientation, displayLeftGroup],
  );

  return (
    <MuiGrid container className={clsx(classes.root, className)}>
      {displayLeftGroup && (
        <MuiGrid className={classes.leftGroup} item xs={12} md={desktopDistribution.left}>
          {leftGroupLogo && leftGroupLogo.original && (
            <div className={classes.leftGroupLogo}>
              <Image image={leftGroupLogo} base="original" size="contain" position="left" />
            </div>
          )}
          {leftGroupDescription && (
            <RichText
              theme={theme}
              content={leftGroupDescription}
              className={clsx('left-group-description--custom', classes.leftGroupDescription)}
              customStyles
            />
          )}
          {leftGroupCta && !isMobile && cta}
        </MuiGrid>
      )}
      <MuiGrid item xs={12} md={desktopDistribution.right}>
        <ul className={clsx(classes.iconTextGrid, multiRowClass)}>
          {iconTextGrid.map(({ icon, text }, id) => {
            const iconLinkProps = mapLinkToProps({ href: icon?.url });
            const ImageWrapper = iconLinkProps.href ? MuiButtonBase : 'div';

            return (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`iconTextCompound_${id}`} className={classes.gridItem}>
                {icon && (
                  <ImageWrapper className={classes.gridIcon} {...iconLinkProps}>
                    <Image image={icon} base="original" size="contain" position="center" />
                  </ImageWrapper>
                )}
                <RichText theme={theme} content={text} />
              </li>
            );
          })}
        </ul>
        {leftGroupCta && isMobile && cta}
      </MuiGrid>
    </MuiGrid>
  );
};

export default memo(IconTexGridComponent);
